import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, Plus, Trash2, Edit2, Star, Eye, EyeOff, X, Check } from 'lucide-react';
import api from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';

const ReviewsList = () => {
    const [reviews, setReviews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingReview, setEditingReview] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        text: '',
        rating: 5,
        isVisible: true
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchReviews = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/review/reviews');
            setReviews(response.data);
        } catch (error) {
            console.error('Error fetching reviews:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Avis - Admin Tekhdem';
        fetchReviews();
    }, [fetchReviews]);

    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet avis ?')) return;
        try {
            await api.delete(`/review/reviews/${id}`);
            setReviews(reviews.filter(r => r._id !== id));
        } catch (error) {
            console.error('Error deleting review:', error);
            alert('Erreur lors de la suppression');
        }
    };

    const handleToggleVisibility = async (review) => {
        try {
            const updatedReview = { ...review, isVisible: !review.isVisible };
            await api.put(`/review/reviews/${review._id}`, updatedReview);
            setReviews(reviews.map(r => r._id === review._id ? updatedReview : r));
        } catch (error) {
            console.error('Error updating review:', error);
        }
    };

    const openModal = (review = null) => {
        if (review) {
            setEditingReview(review);
            setFormData({
                name: review.name,
                role: review.role,
                text: review.text,
                rating: review.rating,
                isVisible: review.isVisible
            });
        } else {
            setEditingReview(null);
            setFormData({
                name: '',
                role: '',
                text: '',
                rating: 5,
                isVisible: true
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (editingReview) {
                const response = await api.put(`/review/reviews/${editingReview._id}`, formData);
                setReviews(reviews.map(r => r._id === editingReview._id ? response.data : r));
            } else {
                const response = await api.post('/review/reviews', formData);
                setReviews([response.data, ...reviews]);
            }
            setShowModal(false);
        } catch (error) {
            console.error('Error saving review:', error);
            alert('Erreur lors de l\'enregistrement');
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredReviews = reviews.filter(review =>
        review.name.toLowerCase().includes(search.toLowerCase()) ||
        review.role.toLowerCase().includes(search.toLowerCase()) ||
        review.text.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Avis Clients</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">Gérez les témoignages affichés sur la page d'accueil</p>
                </div>
                <button
                    onClick={() => openModal()}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-medium transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Nouvel Avis
                </button>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un avis..."
                    className="w-full pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-slate-900 transition-all"
                />
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Note</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Témoignage</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Visible</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center">
                                        <div className="inline-block w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
                                    </td>
                                </tr>
                            ) : filteredReviews.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                                        Aucun avis trouvé
                                    </td>
                                </tr>
                            ) : filteredReviews.map((review) => (
                                <tr key={review._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-slate-900 dark:text-white">{review.name}</div>
                                        <div className="text-xs text-slate-500">{review.role}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-1">
                                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{review.rating}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 max-w-md">
                                            "{review.text}"
                                        </p>
                                    </td>
                                    <td className="px-6 py-4">
                                        <button
                                            onClick={() => handleToggleVisibility(review)}
                                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${review.isVisible
                                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                    : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                                }`}
                                        >
                                            {review.isVisible ? <><Eye className="w-3 h-3" /> Visible</> : <><EyeOff className="w-3 h-3" /> Caché</>}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => openModal(review)}
                                                className="p-2 text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(review._id)}
                                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {createPortal(
                <AnimatePresence>
                    {showModal && (
                        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setShowModal(false)}
                                className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                            />
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]"
                            >
                                <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
                                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                                        {editingReview ? 'Modifier l\'avis' : 'Nouvel avis'}
                                    </h3>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="p-2 -mr-2 text-slate-400 hover:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="overflow-y-auto">
                                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nom du client</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-sm bg-transparent focus:ring-2 focus:ring-slate-900 dark:focus:ring-white outline-none"
                                                placeholder="Ex: Karim B."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Rôle / Titre</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.role}
                                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-sm bg-transparent focus:ring-2 focus:ring-slate-900 dark:focus:ring-white outline-none"
                                                placeholder="Ex: Développeur Full Stack"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Témoignage</label>
                                            <textarea
                                                required
                                                rows={4}
                                                value={formData.text}
                                                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                                                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-sm bg-transparent focus:ring-2 focus:ring-slate-900 dark:focus:ring-white outline-none resize-none"
                                                placeholder="Le contenu de l'avis..."
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Note</label>
                                                <select
                                                    value={formData.rating}
                                                    onChange={(e) => setFormData({ ...formData, rating: Number(e.target.value) })}
                                                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-sm bg-transparent focus:ring-2 focus:ring-slate-900 dark:focus:ring-white outline-none"
                                                >
                                                    {[1, 2, 3, 4, 5].map(num => (
                                                        <option key={num} value={num}>{num} étoiles</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="flex items-center">
                                                <label className="flex items-center gap-2 cursor-pointer mt-6">
                                                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${formData.isVisible ? 'bg-slate-900 border-slate-900 dark:bg-white dark:border-white' : 'border-slate-300'}`}>
                                                        {formData.isVisible && <Check className="w-3 h-3 text-white dark:text-black" />}
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        className="hidden"
                                                        checked={formData.isVisible}
                                                        onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                                                    />
                                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Visible sur le site</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className="pt-4 flex justify-end gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setShowModal(false)}
                                                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                            >
                                                Annuler
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                                            >
                                                {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

export default ReviewsList;
